/**
 * A value that carries a wall clock but not the zone that wall clock belongs to:
 * a dayjs (what `ngx-daterangepicker-material` emits) or a moment.
 */
export interface WallClockLike {
  year(): number;
  month(): number;
  date(): number;
  hour(): number;
  minute(): number;
  second(): number;
  millisecond(): number;
}

/**
 * Turn a date-picker value into a real `Date` in the browser's timezone.
 *
 * `ngx-daterangepicker-material` never hands back a native `Date`. Every value
 * it holds goes through `dayjs(value).utc(true)`, which keeps the picked wall
 * clock but relabels its zone as UTC, so the instant behind the object is that
 * wall clock *read as UTC*. Pick 24/08/2026 10:10 in Cairo (UTC+3 in summer)
 * and the emitted value points at 10:10Z — three hours past the 07:10Z the user
 * meant. Serialising it (`JSON.stringify`, `toISOString`, even `.toDate()`)
 * persists that shifted instant, and comparing it against a real timestamp is
 * off by the browser's UTC offset.
 *
 * Reading the wall clock back out field by field and rebuilding it locally
 * undoes the relabelling. Native `Date`s, ISO strings and epoch numbers are
 * already instants and pass through unchanged, so this is safe to apply at
 * every boundary where a picker value enters a model.
 */
export function toLocalDate(value: unknown): Date | undefined {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }

  if (isWallClockLike(value)) {
    return validOrUndefined(new Date(
      value.year(),
      value.month(),
      value.date(),
      value.hour(),
      value.minute(),
      value.second(),
      value.millisecond()
    ));
  }

  if (value instanceof Date) {
    return validOrUndefined(new Date(value.getTime()));
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return validOrUndefined(new Date(value));
  }

  return undefined;
}

/**
 * Whether `value` is one of those zone-less wall clocks rather than a real instant.
 * Useful at a boundary that must leave everything else — `null` included — untouched.
 */
export function isWallClockLike(value: unknown): value is WallClockLike {
  const candidate = value as Partial<WallClockLike> | null;
  return typeof candidate?.year === 'function'
    && typeof candidate?.hour === 'function'
    && typeof candidate?.millisecond === 'function';
}

function validOrUndefined(date: Date): Date | undefined {
  return isNaN(date.getTime()) ? undefined : date;
}
